import { Heading } from '@chakra-ui/layout'
import { Layout } from 'components/atoms'
import { InputBuild, SelectBuild } from 'components/molecules'
import { useContractContext } from 'contexts/contract'
import { AddressKeys } from 'interfaces/Address'
import { LocationsDTO } from 'models'
import { disableUfFields } from 'pages/Contracts/utils/disableUfFields'
import React, { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { getMunicipio } from 'services/municipio'
import { getCEP } from 'services/viacep'
import formatter from 'utils/formatter'
import { useDebounce } from 'utils/useDebounce'
import isDisableToEditContract from '../../StepsForm/Utils/isDisableToEditContract'
import { FormDebtorValue } from './form'
import { FormDebtorProps } from './interfaces'
import { FormDebtorKeys } from './types'

const FormDebtor: React.FC = () => {
    const {
        register,
        watch,
        formState: { errors },
        setValue,
        setError,
        trigger
    } = useFormContext<{ debtor: FormDebtorProps }>()

    const [searching, setSearching] = useState(false)

    const [location, setLocation] = useState<LocationsDTO[]>([])
    const [lastCep, setLastCep] = useState('')
    const form = useMemo(() => FormDebtorValue(location), [location])

    const { typeContractBoolean } = useContractContext()

    const hasError = (id: FormDebtorKeys) => {
        if (id.includes('endereco')) {
            return (
                errors.debtor?.endereco &&
                errors.debtor.endereco[id.split('.')[1] as AddressKeys]
            )
        } else {
            return (
                errors.debtor &&
                errors.debtor[
                    id as Extract<
                        FormDebtorKeys,
                        'nome' | 'tipoDocumento' | 'documento' | 'telefone'
                    >
                ]
            )
        }
    }

    // Função auxiliar para verificar se o último CEP difere do CEP atual
    function hasCepChanged(cep: string, lastCep: string) {
        return lastCep && lastCep !== cep.replace(/\D/g, '')
    }

    // Atualiza os campos do endereço do devedor com base nos valores fornecidos
    function updateAddressFields({ bairro, logradouro, cep, lastCep }: any) {
        if (bairro) {
            setValue('debtor.endereco.bairro', bairro)
        } else if (hasCepChanged(cep, lastCep)) {
            setValue('debtor.endereco.bairro', '')
        }

        if (logradouro) {
            setValue('debtor.endereco.logradouro', logradouro)
        } else if (hasCepChanged(cep, lastCep)) {
            setValue('debtor.endereco.logradouro', '')
        }
    }

    const consultCEP = useDebounce(async (cep: string) => {
        cep.length === 8 &&
            (await getCEP(cep)
                .then(async response => {
                    const { cep, localidade, logradouro, bairro, uf } = response
                    const locales = await consultMunicipio(uf).then(
                        data => data
                    )

                    const local = `${formatter
                        .clearCharacters(localidade)
                        .toUpperCase()}`

                    const municipio = `${
                        locales?.find(({ municipio }) => municipio === local)
                            ?.codigoTomDetran
                    }|${local}`

                    setSearching(false)

                    setValue('debtor.endereco', {
                        ...watch('debtor.endereco'),
                        cep,
                        uf,
                        municipio
                    })

                    updateAddressFields({ bairro, logradouro, cep, lastCep })
                    trigger('debtor.endereco')
                })
                .catch(() => {
                    setValue('debtor.endereco.municipio', '')
                    setValue('debtor.endereco.cep', '')
                    setError('debtor.endereco.cep', {
                        type: 'validate',
                        message: 'CEP Inválido'
                    })
                })
                .finally(() => {
                    setSearching(false)
                }))
    }, 300)

    const handleCEP = (cep: string) => {
        const value = cep === undefined ? '' : cep.replace(/\D/g, '')
        !typeContractBoolean && setSearching(value.length === 8)
        consultCEP(value)
    }

    const consultMunicipio = async (uf: string) => {
        if (uf) {
            setSearching(true)
            return await getMunicipio(uf)
                .then(({ data }) => {
                    setLocation(data)
                    return data
                })
                .finally(() => setSearching(false))
        } else {
            setLocation([])
        }
    }

    useEffect(() => {
        setValue('debtor.endereco.uf', '')
        setLastCep(watch('debtor.endereco.cep'))
        if (watch('debtor.endereco.cep')) {
            setSearching(true)
            handleCEP(watch('debtor.endereco.cep'))
        } else {
            handleCEP(watch('debtor.endereco.uf'))
        }
    }, [])

    return (
        <Layout
            type="container"
            px={8}
            full
            display="grid"
            gap={8}
            data-testid="form-container"
        >
            <Layout type="row" data-testid="form-heading">
                <Heading size="md" textAlign={{ sm: 'center' }}>
                    Dados do devedor
                </Heading>
            </Layout>
            <Layout type="row" gutter={{ y: 1.5 }} data-testid="form-fields">
                {form.map(({ layout, input, select }, key) => (
                    <Layout {...layout} key={key}>
                        {select ? (
                            <SelectBuild
                                {...select}
                                err={hasError(select.id)}
                                {...register(`debtor.${select.id}`)}
                                isSearching={select.isSearching && searching}
                                value={watch(`debtor.${select.id}`) as string}
                                isDisable={disableUfFields(
                                    typeContractBoolean,
                                    select,
                                    'debtor'
                                )}
                            />
                        ) : (
                            input && (
                                <InputBuild
                                    {...input}
                                    err={hasError(input.id)}
                                    {...register(`debtor.${input.id}`)}
                                    value={
                                        watch(`debtor.${input.id}`) as string
                                    }
                                    isSearching={input.isSearching && searching}
                                    onKeyUp={e => {
                                        input.id === 'endereco.cep' &&
                                            handleCEP(
                                                e.currentTarget.value as string
                                            )
                                    }}
                                    isDisable={
                                        typeContractBoolean &&
                                        isDisableToEditContract[
                                            `debtor.${input.id}`
                                        ]
                                    }
                                />
                            )
                        )}
                    </Layout>
                ))}
            </Layout>
        </Layout>
    )
}

export default FormDebtor
